/**
 * Domain Detection Utility
 * Determines which app surface to render: shop, portal, or admin
 */

export type AppMode = 'shop' | 'portal' | 'admin';

/**
 * Detect which mode the app should run in based on hostname + pathname.
 * - shop.arkxla.com → shop
 * - arkxla.com → shop (marketing/landing)
 * - portal.arkxla.com/admin/* → admin
 * - portal.arkxla.com → portal
 */
export const detectAppMode = (): AppMode => {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  // Allow mode override via ?mode=shop|portal|admin (useful for testing)
  const modeOverride = searchParams.get('mode');
  if (modeOverride === 'shop' || modeOverride === 'portal' || modeOverride === 'admin') {
    return modeOverride;
  }

  // Shop domains
  const shopDomains = ['arkxla.com', 'www.arkxla.com', 'shop.arkxla.com', 'staging-shop.arkxla.com'];
  if (shopDomains.includes(hostname)) return 'shop';

  // Portal domains — check for admin path first
  const portalDomains = ['portal.arkxla.com', 'staging-portal.arkxla.com'];
  if (portalDomains.includes(hostname)) {
    if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'admin';
    return 'portal';
  }

  // Vercel preview / localhost — check pathname for mode
  if (hostname.includes('vercel.app') || hostname === 'localhost') {
    if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'admin';
    return 'shop';
  }

  // Unknown — portal
  return 'portal';
};

/**
 * Check if the current hostname is the shop subdomain (shop.arkxla.com)
 * vs the marketing domain (arkxla.com). Both are AppMode 'shop'.
 */
export const isShopSubdomain = (): boolean => {
  const hostname = window.location.hostname;
  return hostname === 'shop.arkxla.com' || hostname === 'staging-shop.arkxla.com';
};

/**
 * Check if currently running in shop mode
 */
export const isShopMode = (): boolean => {
  return detectAppMode() === 'shop';
};

/**
 * Check if currently running in portal mode
 */
export const isPortalMode = (): boolean => {
  return detectAppMode() === 'portal';
};

/**
 * Check if currently running in admin mode
 */
export const isAdminMode = (): boolean => {
  return detectAppMode() === 'admin';
};

/**
 * Get cross-app URLs, staging-aware.
 */
export const getAppUrls = () => {
  const staging = isStagingDomain();
  return {
    shop: staging ? 'https://staging-shop.arkxla.com' : 'https://shop.arkxla.com',
    marketing: staging ? 'https://staging-shop.arkxla.com' : 'https://arkxla.com',
    portal: staging ? 'https://staging-portal.arkxla.com' : 'https://portal.arkxla.com',
    admin: staging ? 'https://staging-portal.arkxla.com/admin' : 'https://portal.arkxla.com/admin',
  };
};

/**
 * Check if running on a staging domain (staging-*.arkxla.com)
 */
export const isStagingDomain = (): boolean => {
  const hostname = window.location.hostname;
  return hostname.startsWith('staging-');
};

/**
 * Check if running on a production domain (arkxla.com or arkx.com, but NOT staging)
 */
export const isProductionDomain = (): boolean => {
  const hostname = window.location.hostname;
  if (isStagingDomain()) return false;
  return hostname.includes('arkxla.com') || hostname.includes('arkx.com');
};

/**
 * Get the API base URL based on the current domain.
 * Production domains point to the production API.
 * Staging domains point to the staging API.
 * Dev uses REACT_APP_API_URL or falls back to localhost.
 */
export const getApiBaseUrl = (): string => {
  if (isStagingDomain()) {
    return 'https://staging-api.arkxla.com/api/v1';
  }
  if (isProductionDomain()) {
    return 'https://api.arkxla.com/api/v1';
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
};
